// @ts-ignore
import { useObserveSizeContext } from "@pathwright/ui/src/components/observers/ObserveSizeContext"

import { Box, Spinner, VStack, useDisclosure } from "@chakra-ui/react"
import { keyframes } from "@emotion/react"
import AugmentationLayout from "@pathwright/ux/src/components/augmentation/AugmentationLayout"
import AugmentationPositionedToolbar from "@pathwright/ux/src/components/augmentation/AugmentationPositionedToolbar"
import AugmentationRoutedPanel from "@pathwright/ux/src/components/augmentation/AugmentationRoutedPanel"
import {
  useHomeRegistrationsLazyQuery,
  useHomeRegistrationsQuery
} from "../api/generated"
import { PathCreatorContextProvider } from "../path/create/PathCreatorContext"
import { OnComplete } from "../path/create/PathCreatorForm"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { canEditLibrary } from "../user/permissions"
import { useHomeRegistrationVars } from "./HomeRegistrationQueryVars"
import useHomeAugmentationToolbar from "./augmentations/useHomeAugmentationToolbar"
import AddPathButton from "./components/AddPathButton"
import { useResourceCopyConfigLinks } from "./components/prompt/CopyLinkAddPromptCard"
import HomeBackground from "./components/ui/HomeBackground"
import HomeHeader from "./containers/HomeHeader"
import RegistrationsContainer from "./containers/RegistrationsContainer"
import { filterJoinedPaths } from "./tips/HomeTips"

const shrinkIn = keyframes`
	from {
		transform: scale(1.15);
	}
	to {
		transform: scale(1);
	}
`

// Max width of the list.
const maxW = "min(500px, calc(100% - 20px))"

// Reload related data after user completes Path Creator Flow.
function useReloadData(): OnComplete {
  // Just get it working stop-gap for reloading user registrations
  // after successfully completing PathCreatorFlow.
  // NOTE: we could possibly(?) use the onComplete state prop to update the cache instead.
  const [reloadRegistrations] = useHomeRegistrationsLazyQuery({
    variables: useHomeRegistrationVars(),
    fetchPolicy: "network-only"
  })

  return async () => {
    // Once the flow is complete, let's reload the user's registrations.
    await reloadRegistrations()
  }
}

// May want to refactor this, but need to know if primary is loading in order to
// conditionally render the toolbar.
function useIsPrimaryLoading() {
  const registrationVariables = useHomeRegistrationVars()
  const { loading: registrationsLoading } = useHomeRegistrationsQuery({
    variables: registrationVariables
  })
  const [_, { loading: copyLinksLoading }] = useResourceCopyConfigLinks()

  return registrationsLoading || copyLinksLoading
}

// Assess whether to auto show tooltips in PathCreatorForm inputs based on
// whether the user has joined any non-copied paths.
function useShouldAutoShowPathCreatorTips() {
  const registrationVariables = useHomeRegistrationVars()
  const query = useHomeRegistrationsQuery({
    variables: registrationVariables
  })
  const registrations =
    query.data?.me?.registrations?.edges?.map((edge) => edge?.node!) || []
  // If user hasn't joined any paths, then we should auto show tips.
  return filterJoinedPaths(registrations).length <= 0
}

function HomePrimary() {
  const pwContext = usePathwrightContext()
  const registrationVariables = useHomeRegistrationVars()
  const reloadData = useReloadData()
  const autoShowTips = useShouldAutoShowPathCreatorTips()

  const { getRectValue } = useObserveSizeContext()
  const alertHeight = getRectValue("alert.height")

  return (
    <VStack
      w="full"
      minH="100vh"
      h="full"
      minW="min(500px, 100vw)"
      maxW="500px"
      animation={`${shrinkIn} 500ms ease-out`}
      pb={8}
    >
      <Box
        className="HomeHeaderSticky"
        position="sticky"
        top={`${alertHeight}px`}
        w="100%"
        // Overlay other elements which are z-indexed at 1000.
        zIndex={1011}
        // Since we're z-indexing this element and positioning it sticky,
        // it will overlay elements that the user may wish to interact with.
        // Add pointerEvents "all" to any child element the user may wish to interact with.
        pointerEvents="none"
        maxW={maxW}
        minW="min(500px, 100vw)"
      >
        <HomeHeader />
      </Box>
      <VStack pos="relative" w="100%" maxW={maxW} spacing={3} zIndex={1001}>
        <RegistrationsContainer variables={registrationVariables} />
        {canEditLibrary(pwContext) && (
          <AddPathButton onComplete={reloadData} autoShowTips={autoShowTips} />
        )}
      </VStack>
    </VStack>
  )
}

const Home = () => {
  const toolbar = useHomeAugmentationToolbar()
  const disclosure = useDisclosure()
  const navbarHeight = useObserveSizeContext().getRectValue("navbar.height")
  const isPrimaryLoading = useIsPrimaryLoading()

  return (
    <VStack w="100%" mt={`${navbarHeight}px`} py={2} justifyContent="center">
      <HomeBackground />
      {isPrimaryLoading ? (
        <VStack
          align="center"
          w="full"
          p={2}
          minH="50vh"
          justifyContent="center"
        >
          <Spinner size="lg" color="whiteAlpha.900" />
        </VStack>
      ) : (
        <AugmentationLayout
          components={{
            Primary: HomePrimary,
            Panel: AugmentationRoutedPanel,
            Toolbar: AugmentationPositionedToolbar
          }}
          toolbar={toolbar}
          disclosure={disclosure}
          spacing={[40, 10, 10]}
          // padding={20}
          allowOverlay
          panelPosition="far"
        />
      )}
    </VStack>
  )
}

function HomeContainer() {
  const { me } = usePathwrightContext()

  return me ? (
    <PathCreatorContextProvider>
      <Home />
    </PathCreatorContextProvider>
  ) : null
}

export default HomeContainer
